import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useHover } from 'usehooks-ts';
import Icon from '../icon/icon';
import { useTextOverflow } from '../utils/textOverflow';

type ChipVariant = 'primary' | 'secondary' | 'gray' | 'variant';
type ChipSizes = 'xxs' | 'xs' | 'sm';
export type ChipProps = {
    avatar?: string;
    variant?: ChipVariant;
    size?: ChipSizes;
    text: string;
    disabled?: boolean;
    onClose?: (evt?: React.MouseEvent) => void;
    className?: string;
    color?: string;
    'data-testid'?: string;
    'data-test-id'?: string;
};

export function Chip(props: ChipProps) {
    const baseClasses =
        'flex justify-center items-center m-1 py-1 px-2 rounded-full bg-gray-50 max-w-full cursor-auto ';
    const sizeClasses: Record<ChipSizes, string> = {
        sm: 'h-[48px]',
        xs: 'h-[32px]',
        xxs: 'h-[20px]',
    };
    const variantClasses: Record<ChipVariant, string> = {
        gray: 'hover:bg-gray-200 first:active:text-gray-400',
        primary: 'hover:bg-blue-50 pointer active:border border-blue-200 first:active:text-primary-500',
        secondary: 'hover:bg-orange-50 active:border border-orange-200 first:active:text-secondary-200',
        variant: 'hover:bg-variant-50 active:border border-variant-200 first:active:text-variant-200',
    };

    const variantCloseClasses: Record<ChipVariant, string> = {
        gray: 'bg-gray-100 hover:bg-gray-100',
        primary: 'bg-gray-100 hover:bg-blue-200',
        secondary: 'bg-gray-100 hover:bg-orange-200',
        variant: 'bg-variant-100 hover:bg-variant-200',
    };

    const container = clsx(
        baseClasses,
        sizeClasses[props.size || 'xs'],
        variantClasses[props.variant || 'primary'],
        props.disabled && 'text-gray-100 cursor-not-allowed pointer-events-none',
    );
    const closeButtonContainer = clsx(
        'flex justify-center items-center rounded-full ml-2 cursor-pointer',
        variantCloseClasses[props.variant || 'primary'],
        props.size === 'xxs' ? 'w-3.5 h-3.5' : 'w-4 h-4',
        `${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`,
    );

    function getLuminance(hex: string): number {
        if (!hex.includes('#')) return 0;
        hex = hex.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const a = [r, g, b].map((v) => {
            v /= 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });

        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    }

    function isDarkColor(hex: string): boolean {
        return getLuminance(hex) < 0.5;
    }

    const textColor = isDarkColor(props.color || '#FFFFFF') ? 'text-white' : 'text-black';

    const { contentRef, isOverflowing } = useTextOverflow(props.text, true);
    const isHover = useHover(contentRef);

    const elementMeasures = contentRef.current?.getBoundingClientRect();
    return (
        <button
            className={twMerge(container, props.className)}
            data-testid={props['data-testid'] || `${props.text}-chip`}
            data-test-id={props['data-test-id'] || `${props.text}-chip`}
            style={{ backgroundColor: props.color }}
            type="button"
        >
            {props.avatar && (
                <div slot="avatar">
                    <div className="flex relative w-6 h-6 bg-gray-200 justify-center items-center m-1 mr-2 ml-0 my-0 text-lg rounded-full ">
                        <img className="rounded-full" alt="" src={props.avatar} />
                    </div>
                </div>
            )}
            <div
                ref={contentRef}
                className={` flex-initial overflow-hidden whitespace-nowrap ${textColor} text-ellipsis`}
            >
                {props.text}
            </div>
            {elementMeasures && isOverflowing && isHover && (
                <div
                    className={twMerge(
                        'absolute whitespace-nowrap text-xs hidden bg-slate-700 rounded p-1 z-[999] text-white',
                        isHover && isOverflowing && 'block',
                    )}
                    style={{
                        top: elementMeasures?.top - elementMeasures.height - 12,
                        left: elementMeasures?.left + elementMeasures.width / 8,
                    }}
                >
                    {props.text}
                </div>
            )}
            <button type="button" className={closeButtonContainer} onClick={props.onClose} disabled={props.disabled}>
                <Icon name="ri-close-line" size={16} data-testid={'close-icon'} />
            </button>
        </button>
    );
}

export default Chip;
